import {
  Menu,
  MenuList,
  MenuItem,
  MenuButton,
  Text,
  Box,
  Flex,
  useMediaQuery,
  Image,
} from '@chakra-ui/react';
import { useMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AiOutlineLock } from 'react-icons/ai';

import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
import { getModelUsers, putModelUsers } from 'redux/HeaderSlice';
import { getUserData } from 'redux/UserSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { font, neutral, radius, text as textToken } from 'styles/revampTokens';

const AVATAR_GRADIENT =
  'linear-gradient(90.09deg, rgb(116, 91, 227) 0%, rgb(95, 147, 206) 100.42%)';
const YOU_BADGE_COLOR = '#3BA7FA';

const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

interface Properties {
  shareUser: any;
  modelOwner: any;
  userPermission?: any;
}

const UserDetails = ({ shareUser, modelOwner, userPermission }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector(getUserData);
  const [isMobile] = useMediaQuery('(max-width: 575px)');
  const match = useMatch({ path: 'model/:modelId/', end: false });
  const modelId = match?.params?.modelId;

  const textStyle = {
    fontFamily: 'regular_roboto',
    fontWeight: 400,
  };

  const onChangePermissions = (editUser: any, permission: string) => {
    dispatch(
      putModelUsers({
        modelId,
        request_body: {
          user_id: editUser.user_id,
          role: permission,
        },
      }),
    ).then(() => {
      dispatch(getModelUsers(Number(modelId)));
    });
  };

  const displayName =
    shareUser?.orgnization_name === 'anonymous' ? 'Guest' : shareUser?.firstname ?? '';
  const isYou = shareUser?.user_id === user?.id;

  return (
    <Flex justify={'space-between'} align={'center'} w={'full'} gap={3}>
      <Flex align={'center'} gap={3} minW={0}>
        {shareUser?.profile_image_url ? (
          <Image
            border='1px solid'
            borderColor={neutral.border}
            borderRadius={'full'}
            src={shareUser?.profile_image_url}
            width={'32px'}
            height={'32px'}
            objectFit='cover'
            flexShrink={0}
          />
        ) : (
          <Flex
            width={'32px'}
            height={'32px'}
            borderRadius={'full'}
            bgImage={AVATAR_GRADIENT}
            color={textToken.inverse}
            alignItems={'center'}
            justifyContent={'center'}
            fontFamily={font.display}
            fontSize={'11px'}
            fontWeight={600}
            flexShrink={0}
          >
            {initialsOf(displayName || shareUser?.username || '?')}
          </Flex>
        )}
        <Box minW={0}>
          <Flex align='center' gap='4px'>
            <Text {...textStyle} color={textToken.primary} fontSize='13px' isTruncated>
              {displayName}
              {shareUser?.is_in_same_org ? '' : ' (External)'}
            </Text>
            {isYou && (
              <Box
                bg={YOU_BADGE_COLOR}
                color={textToken.inverse}
                fontSize='10px'
                fontWeight={500}
                lineHeight='16px'
                px='6px'
                borderRadius='4px'
                flexShrink={0}
              >
                You
              </Box>
            )}
          </Flex>
          <Text {...textStyle} color={textToken.secondary} fontSize='11px' isTruncated>
            {shareUser?.username}
          </Text>
        </Box>
      </Flex>
      <Menu>
        <MenuButton
          // Native `disabled` (not Chakra's `isDisabled`, which only lands when
          // rendering `as={Button}`) — a disabled <button> also stops the menu
          // from opening, and backs the `&:disabled` rules below.
          disabled={
            shareUser?.role === 'owner' ||
            shareUser?.orgnization_name === 'anonymous' ||
            !shareUser?.is_in_same_org ||
            modelOwner?.user_id !== user?.id ||
            !(userPermission === 'execute')
          }
          // Deliberately NOT `as={Button}`: the app's Button theme defaults to
          // `{ variant: 'outlined', size: 'lg' }`, whose 24px radius and fixed
          // width leak through, and `variant='unstyled'` then forces
          // `display: inline`, which breaks the label/caret row. A plain
          // MenuButton is just a <button> we can style directly.
          border='1px solid'
          borderColor={neutral.border}
          borderRadius='7px'
          bg={neutral.surface}
          w={isMobile ? '82px' : '92px'}
          h='30px'
          px='10px'
          color={textToken.primary}
          textTransform={'capitalize'}
          {...textStyle}
          fontSize={'12px'}
          fontWeight={500}
          flexShrink={0}
          transition='background 0.15s ease, border-color 0.15s ease'
          _hover={{ bg: neutral.surfaceHover, borderColor: neutral.borderStrong }}
          css={`
            &:disabled {
              opacity: 1;
              cursor: not-allowed;
            }
            &:disabled:hover {
              background: ${neutral.surface};
              border-color: ${neutral.border};
            }
          `}
        >
          {/* The row lives inside the button, so its layout no longer depends
              on whatever display mode the button itself ends up with. */}
          <Flex align='center' justify='space-between' gap='6px' w='full'>
            <Box as='span' isTruncated>
              {shareUser?.role}
            </Box>
            {shareUser?.role === 'view' ? (
              <AiOutlineLock size={12} color={textToken.secondary} />
            ) : (
              <FigmaTriangleDown width={9} height={5} color={textToken.primary} />
            )}
          </Flex>
        </MenuButton>
        <MenuList minW='120px' borderRadius={radius.overlay} borderColor={neutral.border}>
          {modelOwner?.user_id !== user?.id && (
            <MenuItem fontSize='13px' onClick={() => onChangePermissions(shareUser, 'owner')}>
              Owner
            </MenuItem>
          )}
          <MenuItem fontSize='13px' onClick={() => onChangePermissions(shareUser, 'edit')}>
            Edit
          </MenuItem>
          <MenuItem fontSize='13px' onClick={() => onChangePermissions(shareUser, 'view')}>
            View
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};

export default UserDetails;
